// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
// AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
import interpolateMonetaryValue from './interpolateMonetaryValue'
import { buildRowLookup, isNegativeRow } from '../analysisHelpers/matrixRowLookup'
import mirrorScore from '../analysisHelpers/mirrorScore'
import toMillis from '../measurementSignals/toMillis'
import attributedValue from './applyRekenregels'
import resolveInterventionDose from './resolveInterventionDose'

/**
 * @typedef {object} MsiResponseRecord
 * @property {number | string} MomentPosition - Measure-moment position (enriched by fetchHierarchyData)
 * @property {string} RowTitle - Matrix row title (= MSI question)
 * @property {string} Input - Chosen score as string, "1".."5"
 * @property {string} Persona - Persona ID when the response is persona-coupled, '' otherwise
 * @property {string} FormID - Submission session id (unique per submission)
 * @property {*} [Timestamp] - Moment van de inzending; de laatste is het eind van een traject zonder einddatum
 */

/**
 * @typedef {object} MsiPersonaDelta
 * @property {string} persona - Persona ID
 * @property {number} firstAvg - Average (flipped) score at the persona's first moment
 * @property {number} lastAvg - Average (flipped) score at the persona's last moment
 * @property {number} delta - € value created: interp(lastAvg) − interp(firstAvg)
 * @property {number|null} horizon - Tijdshorizon in jaren (rekenregels), null = onbekend
 * @property {number|null} attributed - Toegerekende waarde: delta × impactclaim × factor; null = niet doorgerekend
 * @property {string|null} doseReason - Waarom er geen horizon is
 * @property {{ months: string|null, hours: string|null }} doseSource - Herkomst van duur en uren
 */

/**
 * @typedef {object} MsiAnonymousDelta
 * @property {number} respondentCount - Unique respondents (FormID) at the last moment
 * @property {number} firstAvg - Anonymous average score at the first moment
 * @property {number} lastAvg - Anonymous average score at the last moment
 * @property {number} delta - € value created: (interp(lastAvg) − interp(firstAvg)) × respondentCount
 * @property {number|null} horizon - Tijdshorizon uit de standaard van de organisatie
 * @property {number|null} attributed - Toegerekende waarde, null = niet doorgerekend
 * @property {string|null} doseReason - Waarom er geen horizon is
 */

/**
 * @typedef {object} MsiEffectValue
 * @property {boolean} noScores - True when the effect has no usable monetary scores (contributes €0, visibly)
 * @property {number} momentCount - Distinct measured moments in the usable records
 * @property {number} deltaTotal - Sum of persona deltas + anonymous delta (0 when fewer than 2 moments)
 * @property {Array<MsiPersonaDelta>} perPersona - Personas measured at ≥2 moments
 * @property {MsiAnonymousDelta | null} anonymous - Anonymous aggregate, null without ≥2 anonymous moments
 * @property {number | null} stateValue - Last-moment situation value: interp(avg) × unique respondents
 * @property {number | null} firstAvg - Overall average (flipped) score at the first measured moment — the prognose baseline
 * @property {number} lastRespondents - Unique respondents at the last measured moment
 * @property {number} attributedTotal - Som van de toegerekende waarden die doorgerekend konden worden
 * @property {number} notAttributedCount - Deelnemers + anonieme respondenten waarvan het verschil niet doorgerekend kon worden
 * @property {Object<string, number>} notAttributedReasons - Dezelfde telling per reden
 */

// Average of the (flipped) numeric values per moment position → { pos: avg }
const averagesByMoment = (records) => {
    const buckets = {}
    records.forEach((r) => {
        const bucket = (buckets[r.pos] ||= { sum: 0, count: 0 })
        bucket.sum += r.value
        bucket.count += 1
    })
    const averages = {}
    Object.keys(buckets).forEach((pos) => {
        averages[pos] = buckets[pos].sum / buckets[pos].count
    })
    return averages
}

const sortedPositions = (averages) => Object.keys(averages).map(Number).sort((a, b) => a - b)

// Laatste inzendmoment in een set records (ms), null als er geen tijdstempel is
const latestMs = (records) => records.reduce((max, r) => (r.ms !== null && (max === null || r.ms > max) ? r.ms : max), null)

/**
 * Turns the response records of ONE MSI effect into euro values using the
 * meetstandaard-API `scores[]` of that effect. Value model: delta between the
 * first and last measured moment (yearly situation values, so impact = change).
 * Persona-coupled responses get a per-persona delta; anonymous responses can't
 * be paired across moments (FormID is unique per submission), so they get an
 * aggregate delta × respondent count. Respondents are deduped by FormID —
 * matrix questions store one doc per row per respondent.
 *
 * @param {object} params
 * @param {Array<MsiResponseRecord>} params.records - Response records as returned by fetchHierarchyData
 * @param {Array<{ Title: string, posNeg?: string }>} params.rowDefs - Raw MatrixQuestionRows defs (NOT the collapsed 'Gemiddelde' set)
 * @param {number} params.lowerScale - Scale lower bound (ReachStart, 1 for MSI)
 * @param {number} params.upperScale - Scale upper bound (ReachEnd, 5 for MSI)
 * @param {Array<{ score: number, monetaryValue: number }>} params.scores - meetstandaard-API `scores[]` for this effect
 * @param {Object<string, object>} [params.personasById] - Personas-docs op ID, voor de Traject-velden (rekenregels)
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [params.orgDefault] - CompagnyMeta.DefaultInterventionDose
 * @returns {MsiEffectValue}
 */
const computeMsiEffectValue = ({ records, rowDefs, lowerScale = 1, upperScale = 5, scores, personasById = {}, orgDefault = null } = {}) => {
    const noScores = interpolateMonetaryValue(lowerScale, scores) === null

    // Valence via RowID; the frozen RowTitle is only the legacy fallback, so a
    // reworded question keeps producing the same euros.
    const rowLookup = buildRowLookup(rowDefs)

    // Usable records: numeric non-empty Input, known moment position; flip negative rows
    const usable = []
    ;(Array.isArray(records) ? records : []).forEach((r) => {
        if (r?.Input == null || r.Input === '' || (!r.RowTitle && !r.RowID)) return
        const num = Number(r.Input)
        if (!Number.isFinite(num)) return
        const pos = typeof r.MomentPosition === 'number' ? r.MomentPosition : parseInt(r.MomentPosition, 10)
        if (!Number.isFinite(pos)) return
        usable.push({
            pos,
            value: mirrorScore(num, { lowerScale, upperScale }, isNegativeRow(rowLookup, r)),
            // Legacy garbage: ?persona=undefined in collection URLs stamped the
            // STRING "undefined" — those respondents are anonymous, not one
            // shared deelnemer.
            persona: r.Persona && r.Persona !== 'undefined' ? r.Persona : '',
            formId: r.FormID || '',
            ms: toMillis(r.Timestamp),
        })
    })

    const momentCount = new Set(usable.map((r) => r.pos)).size

    if (noScores || usable.length === 0) {
        return {
            noScores, momentCount, deltaTotal: 0, perPersona: [], anonymous: null, stateValue: null, firstAvg: null, lastRespondents: 0,
            attributedTotal: 0, notAttributedCount: 0, notAttributedReasons: {},
        }
    }

    // --- Persona-coupled: delta per persona, needs ≥2 measured moments ---
    const byPersona = {}
    usable.filter((r) => r.persona).forEach((r) => {
        ;(byPersona[r.persona] ||= []).push(r)
    })

    const perPersona = []
    Object.keys(byPersona).forEach((persona) => {
        const averages = averagesByMoment(byPersona[persona])
        const positions = sortedPositions(averages)
        if (positions.length < 2) return
        const firstAvg = averages[positions[0]]
        const lastAvg = averages[positions[positions.length - 1]]
        const delta = interpolateMonetaryValue(lastAvg, scores) - interpolateMonetaryValue(firstAvg, scores)
        // Eind van een traject zonder einddatum = de laatste meting in dit verschil (besluit R3)
        const lastMs = latestMs(byPersona[persona].filter((r) => r.pos === positions[positions.length - 1]))
        const dose = resolveInterventionDose({ persona: personasById[persona] || null, lastMeasurementMs: lastMs, orgDefault })
        perPersona.push({
            persona,
            firstAvg,
            lastAvg,
            delta,
            horizon: dose.horizon,
            attributed: attributedValue(delta, dose.horizon),
            doseReason: dose.reason,
            doseSource: dose.source,
        })
    })

    // --- Anonymous: aggregate delta × respondents at the last moment ---
    const anonymousRecords = usable.filter((r) => !r.persona)
    let anonymous = null
    const anonAverages = averagesByMoment(anonymousRecords)
    const anonPositions = sortedPositions(anonAverages)
    if (anonPositions.length >= 2) {
        const firstAvg = anonAverages[anonPositions[0]]
        const lastAvg = anonAverages[anonPositions[anonPositions.length - 1]]
        const lastPos = anonPositions[anonPositions.length - 1]
        const respondentCount = new Set(
            anonymousRecords.filter((r) => r.pos === lastPos).map((r) => r.formId),
        ).size
        const delta = (interpolateMonetaryValue(lastAvg, scores) - interpolateMonetaryValue(firstAvg, scores)) * respondentCount
        // Anoniem is er geen traject: alleen de standaard van de organisatie
        const dose = resolveInterventionDose({ persona: null, orgDefault })
        anonymous = {
            respondentCount,
            firstAvg,
            lastAvg,
            delta,
            horizon: dose.horizon,
            attributed: attributedValue(delta, dose.horizon),
            doseReason: dose.reason,
        }
    }

    const deltaTotal = perPersona.reduce((sum, p) => sum + p.delta, 0) + (anonymous ? anonymous.delta : 0)

    // Toegerekende waarde: alleen wat doorgerekend kon worden telt op; de rest
    // wordt per persoon geteld, nooit als 0 meegeteld (besluit R2).
    let attributedTotal = 0
    let notAttributedCount = 0
    const notAttributedReasons = {}
    const tally = (entry, people) => {
        if (entry.attributed !== null) {
            attributedTotal += entry.attributed
            return
        }
        notAttributedCount += people
        notAttributedReasons[entry.doseReason] = (notAttributedReasons[entry.doseReason] || 0) + people
    }
    perPersona.forEach((p) => tally(p, 1))
    if (anonymous) tally(anonymous, anonymous.respondentCount)

    // --- State value: situation value at the last measured moment (all respondents) ---
    const allAverages = averagesByMoment(usable)
    const allPositions = sortedPositions(allAverages)
    const lastPos = allPositions[allPositions.length - 1]
    const lastRespondents = new Set(
        usable.filter((r) => r.pos === lastPos).map((r) => r.persona || r.formId),
    ).size
    const stateValue = interpolateMonetaryValue(allAverages[lastPos], scores) * lastRespondents

    // Overall first-moment average: the baseline the goal prognose measures from.
    const firstAvg = allAverages[allPositions[0]]

    return {
        noScores, momentCount, deltaTotal, perPersona, anonymous, stateValue, firstAvg, lastRespondents,
        attributedTotal, notAttributedCount, notAttributedReasons,
    }
}

export default computeMsiEffectValue
